import { FunctionComponent } from "react";

import {
	MultiAutoComplete,
	MultiAutoCompleteItem,
} from "@/components/MultiAutoComplete/MultiAutoComplete";
import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";

import { ConfigField } from "./ConfigField";

/** A fixed-option filter (seniority, department, employee size, …) over a string[]. */
export const OptionMultiField: FunctionComponent<{
	label: string;
	placeholder: string;
	options: MultiAutoCompleteItem[];
	values: string[];
	full?: boolean;
	error?: string;
	onChange: (values: string[]) => void;
}> = ({ label, placeholder, options, values, full, error, onChange }) => (
	<ConfigField error={error} full={full} label={label}>
		<MultiAutoComplete
			stayOn
			data={options}
			handleSelect={(items) => onChange((items ?? []).map((item) => String(item.id)))}
			placeholder={placeholder}
			selectedItems={values.map((value) => ({
				id: value,
				title: options.find((option) => String(option.id) === value)?.title ?? value,
			}))}
		/>
	</ConfigField>
);

/**
 * A free-text filter with suggestions — job titles, keywords, seed domains.
 * Anything typed is kept (the upstream filters are free-text queries), the
 * lookup only proposes the spellings the database actually knows.
 */
export const SuggestingTagField: FunctionComponent<{
	label: string;
	placeholder: string;
	values: string[];
	fetchFn: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	/** Cleans a typed value before it becomes a tag (e.g. a pasted domain URL). */
	normalizeValue?: (value: string) => string;
	required?: boolean;
	full?: boolean;
	hint?: string;
	error?: string;
	testId?: string;
	onChange: (values: string[]) => void;
}> = ({
	label,
	placeholder,
	values,
	fetchFn,
	normalizeValue,
	required,
	full,
	hint,
	error,
	testId,
	onChange,
}) => (
	<ConfigField error={error} full={full} hint={hint} label={label} required={required}>
		<CompanyEnrichAutoComplete
			allowCustomValues
			stayOn
			fetchFn={fetchFn}
			handleSelect={(items) => onChange((items ?? []).map((item) => item.id))}
			normalizeCustomValue={normalizeValue}
			placeholder={placeholder}
			selectedItems={values.map((value) => ({ id: value, title: value }))}
			testId={testId}
		/>
	</ConfigField>
);

/** An async-lookup filter whose values are stored as ids (country codes, industries). */
export const LookupMultiField: FunctionComponent<{
	label: string;
	placeholder: string;
	values: string[];
	titleOf: (id: string) => string;
	fetchFn: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	full?: boolean;
	error?: string;
	testId?: string;
	onChange: (values: string[]) => void;
}> = ({ label, placeholder, values, titleOf, fetchFn, full, error, testId, onChange }) => (
	<ConfigField error={error} full={full} label={label}>
		<CompanyEnrichAutoComplete
			stayOn
			fetchFn={fetchFn}
			handleSelect={(items) => onChange((items ?? []).map((item) => item.id))}
			placeholder={placeholder}
			selectedItems={values.map((value) => ({ id: value, title: titleOf(value) }))}
			testId={testId}
		/>
	</ConfigField>
);
